/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch } from 'react-redux';
import type { ServiceInterface } from '@opengeoweb/shared';
import {
  layerActions,
  mapSelectors,
  layerTypes,
  serviceSelectors,
  layerSelectors,
  uiSelectors,
  uiTypes,
} from '@opengeoweb/store';
import { LayerType, webmapUtils } from '@opengeoweb/webmap';
import type { DeleteLayerParams } from './LayerAddRemoveButton';
import { LayerList } from './LayerList';
import { useAppSelector, layerSelectSelectors } from '../../store';
import type { LayerWithServiceName } from '../../store/types';

interface LayerListConnectProps {
  mapId: string;
  layerSelectHeight: number;
  serviceListHeight: number;
  layerSelectWidth: number;
}

interface AddLayerParams {
  serviceUrl: string;
  layerName: string;
  serviceType?: ServiceInterface;
}

export const LayerListConnect: React.FC<LayerListConnectProps> = ({
  mapId,
  layerSelectHeight,
  serviceListHeight,
  layerSelectWidth,
}: LayerListConnectProps) => {
  const dispatch = useDispatch();

  const services = useAppSelector((store) =>
    serviceSelectors.getServices(store),
  );

  const mapLayers = useAppSelector((store): layerTypes.Layer[] =>
    mapSelectors.getMapLayersWithoutDimensionCurrentValue(store, mapId),
  );

  const allLayers = useAppSelector((store) =>
    layerSelectors.getAllLayers(store),
  );

  const allMapIds = useAppSelector((store) => mapSelectors.getAllMapIds(store));

  const addLayer = (
    { serviceUrl, layerName, serviceType }: AddLayerParams,
    mapId: string,
  ): void => {
    const layer = {
      service: serviceUrl,
      name: layerName,
      id: webmapUtils.generateLayerId(),
      layerType:
        serviceType === 'STAC'
          ? LayerType.stacGeoTIFFLayer
          : LayerType.mapLayer,
    } as layerTypes.Layer;

    addMapLayer({
      mapId,
      layerId: layer.id,
      layer,
      origin: layerTypes.LayerActionOrigin.layerManager,
    } as layerTypes.AddLayerPayload);
  };

  const addMapLayer = React.useCallback(
    ({ mapId, layerId, layer, origin }: layerTypes.AddLayerPayload) =>
      dispatch(
        layerActions.addLayer({
          mapId,
          layerId,
          layer,
          origin,
        }),
      ),
    [dispatch],
  );

  const addLayerToAllMaps = ({
    serviceUrl,
    layerName,
    serviceType,
  }: AddLayerParams): void => {
    allMapIds.forEach((mapId) => {
      if (
        allLayers.find(
          (layer) =>
            layer.name === layerName &&
            layer.service === serviceUrl &&
            layer.mapId === mapId,
        )
      ) {
        return;
      }
      addLayer({ serviceUrl, layerName, serviceType }, mapId);
    });
  };

  const deleteLayer = React.useCallback(
    ({ layerId, layerIndex }: DeleteLayerParams) =>
      dispatch(
        layerActions.layerDelete({
          mapId,
          layerId,
          layerIndex,
          origin: layerTypes.LayerActionOrigin.layerManager,
        }),
      ),
    [dispatch, mapId],
  );

  const removeLayerFromAllMaps = React.useCallback(
    (layerId: string) => {
      if (!layerId) {
        return;
      }
      const layerToRemove = allLayers.find((layer) => layer.id === layerId);
      layerToRemove?.name &&
        layerToRemove?.service &&
        allLayers
          .filter(
            (layer) =>
              layer.name === layerToRemove.name &&
              layer.service === layerToRemove.service,
          )
          .forEach((layer) => {
            layer.mapId &&
              layer.id &&
              dispatch(
                layerActions.layerDelete({
                  mapId: layer.mapId,
                  layerId: layer.id,
                }),
              );
          });
    },
    [dispatch, allLayers],
  );

  const filteredLayers: LayerWithServiceName[] = useAppSelector((store) =>
    layerSelectSelectors.getFilteredLayers(store),
  );

  const searchFilter = useAppSelector((store) =>
    layerSelectSelectors.getSearchFilter(store),
  );

  const isLayerInfoVisibleInStore = useAppSelector((store) =>
    uiSelectors.getisDialogOpen(store, uiTypes.DialogTypes.LayerInfo),
  );

  const currentLayerInfo = useAppSelector((store) =>
    layerSelectors.getActiveLayerInfo(store),
  );

  const onToggleActiveLayer = React.useCallback(
    ({
      serviceUrl,
      layerName,
      isActiveLayer,
    }: AddLayerParams & { isActiveLayer: boolean }): void => {
      if (!isActiveLayer) {
        dispatch(
          layerActions.showLayerInfo({
            layerName,
            serviceUrl,
            mapId,
          }),
        );
      } else {
        dispatch(layerActions.hideLayerInfo());
      }
    },
    [mapId, dispatch],
  );

  return (
    <LayerList
      addLayer={(params) => addLayer(params, mapId)}
      addLayerToAllMaps={addLayerToAllMaps}
      allLayers={allLayers}
      allMapIds={allMapIds}
      currentLayerInfo={currentLayerInfo!}
      deleteLayer={deleteLayer}
      filteredLayers={filteredLayers}
      isLayerInfoVisibleInStore={isLayerInfoVisibleInStore}
      layerSelectHeight={layerSelectHeight}
      layerSelectWidth={layerSelectWidth}
      mapLayers={mapLayers}
      onToggleActiveLayer={onToggleActiveLayer}
      removeLayerFromAllMaps={removeLayerFromAllMaps}
      searchFilter={searchFilter}
      serviceListHeight={serviceListHeight}
      services={services}
    />
  );
};
